var _react_refresh_temp_1, _react_refresh_temp_2, _react_refresh_temp_3, _react_refresh_temp_4, _react_refresh_temp_5;
var _react_refresh_temp_6;
_react_refresh_temp_6 = $RefreshSig$();
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import throttle from 'lodash/throttle';
import { createSelector } from '@reduxjs/toolkit';
import { Excalidraw, Footer, MainMenu, getSceneVersion, } from '@excalidraw/excalidraw';
import { store, useAppSelector } from '../../store';
import { useCallbackRefState } from './helpers/hooks/useCallbackRefState';
import { reconcileElements, } from './helpers/reconciliation';
import { participantsSelector } from '../../store/slices/participantSlice';
import { useTranslation } from 'react-i18next';
import { fetchFileWithElm } from './helpers/fileReader';
import { broadcastAppStateChanges, broadcastMousePointerUpdate, broadcastSceneOnChange, sendRequestedForWhiteboardData, sendWhiteboardDataAsDonor, } from './helpers/handleRequestedWhiteboardData';
import FooterUI from './footerUI';
import usePreviousFileId from './helpers/hooks/usePreviousFileId';
import usePreviousPage from './helpers/hooks/usePreviousPage';
import ManageFiles from './manageFiles';
import { addPreloadedLibraryItems, displaySavedPageData, } from './helpers/utils';
import './style.scss';
const whiteboardSelector = (state) => state.whiteboard;
const lockWhiteboardSelector = createSelector(_react_refresh_temp_1 = (state) => { var _a, _b; return (_b = (_a = state.session.currentUser) === null || _a === void 0 ? void 0 : _a.metadata) === null || _b === void 0 ? void 0 : _b.lock_settings; }, (lock_settings) => lock_settings === null || lock_settings === void 0 ? void 0 : lock_settings.lock_whiteboard);
$RefreshReg$(_react_refresh_temp_1, "lockWhiteboardSelector$createSelector");
const isPresenterSelector = createSelector(_react_refresh_temp_2 = (state) => { var _a; return (_a = state.session.currentUser) === null || _a === void 0 ? void 0 : _a.metadata; }, (metadata) => metadata === null || metadata === void 0 ? void 0 : metadata.is_presenter);
$RefreshReg$(_react_refresh_temp_2, "isPresenterSelector$createSelector");
const themeSelector = createSelector(_react_refresh_temp_3 = (state) => state.roomSettings, (roomSettings) => roomSettings.theme);
$RefreshReg$(_react_refresh_temp_3, "themeSelector$createSelector");
const screenWidthSelector = createSelector(_react_refresh_temp_4 = (state) => state.bottomIconsActivity, (bottomIconsActivity) => bottomIconsActivity.screenWidth);
$RefreshReg$(_react_refresh_temp_4, "screenWidthSelector$createSelector");
const Whiteboard = ({ onReadyExcalidrawAPI }) => {
    _react_refresh_temp_6();
    const currentUser = store.getState().session.currentUser;
    const currentRoom = store.getState().session.currentRoom;
    const CURSOR_SYNC_TIMEOUT = 33;
    const collaborators = new Map();
    const { i18n } = useTranslation();
    const [excalidrawAPI, excalidrawRefCallback] = useCallbackRefState();
    const [viewModeEnabled, setViewModeEnabled] = useState(true);
    const theme = useAppSelector(themeSelector);
    const [lastBroadcastOrReceivedSceneVersion, setLastBroadcastOrReceivedSceneVersion,] = useState(-1);
    const whiteboard = useAppSelector(whiteboardSelector);
    const previousFileId = usePreviousFileId(whiteboard.currentWhiteboardOfficeFileId);
    const previousPage = usePreviousPage(whiteboard.currentPage);
    const participants = useAppSelector(participantsSelector.selectAll);
    const lockWhiteboard = useAppSelector(lockWhiteboardSelector);
    const isPresenter = useAppSelector(isPresenterSelector);
    const [fetchedData, setFetchedData] = useState(false);
    const [currentWhiteboardWidth, setCurrentWhiteboardWidth] = useState(0);
    const screenWidth = useAppSelector(screenWidthSelector);
    useEffect(() => {
        var _a, _b, _c, _d;
        const s = store.getState();
        const isPresenter = (_b = (_a = s.session.currentUser) === null || _a === void 0 ? void 0 : _a.metadata) === null || _b === void 0 ? void 0 : _b.is_presenter;
        if (excalidrawAPI) {
            if (isPresenter) {
                // if presenter then we'll fetch storage to display after initialize excalidraw
                setTimeout(() => {
                    const currentPage = s.whiteboard.currentPage;
                    displaySavedPageData(excalidrawAPI, isPresenter, currentPage);
                }, 100);
            }
        }
        else if (!(currentUser === null || currentUser === void 0 ? void 0 : currentUser.isRecorder) &&
            !((_d = (_c = currentRoom.metadata) === null || _c === void 0 ? void 0 : _c.default_lock_settings) === null || _d === void 0 ? void 0 : _d.lock_whiteboard)) {
            setViewModeEnabled(false);
        }
    }, [excalidrawAPI]);
    // keep looking for request from other users & send data
    useEffect(() => {
        if (!fetchedData && excalidrawAPI) {
            // get initial data from other users
            // who had joined before me
            sendRequestedForWhiteboardData();
            setFetchedData(true);
        }
        if (whiteboard.requestedWhiteboardData.requested && excalidrawAPI) {
            sendWhiteboardDataAsDonor(excalidrawAPI, whiteboard.requestedWhiteboardData.sendTo);
        }
    }, [excalidrawAPI, whiteboard.requestedWhiteboardData, fetchedData]);
    // if whiteboard file ID change this mean new office file was uploaded,
    // so we'll clean the canvas.
    useEffect(() => {
        if (excalidrawAPI &&
            whiteboard.currentWhiteboardOfficeFileId !== previousFileId) {
            setLastBroadcastOrReceivedSceneVersion(-1);
            excalidrawAPI.updateScene({
                elements: [],
            });
            excalidrawAPI.addFiles([]);
            excalidrawAPI.history.clear();
        }
    }, [excalidrawAPI, whiteboard.currentWhiteboardOfficeFileId, previousFileId]);
    // for adding users to canvas as collaborators
    useEffect(() => {
        if (!excalidrawAPI) {
            return;
        }
        participants.forEach((participant) => {
            if (participant.metadata.is_admin) {
                if (!collaborators.has(participant.userId)) {
                    collaborators.set(participant.userId, {});
                }
            }
        });
        // now check if any user still exist after disconnected
        collaborators.forEach((_, i) => {
            const found = participants.find((p) => p.userId === i);
            if (!found) {
                collaborators.delete(i);
            }
        });
        excalidrawAPI.updateScene({ collaborators });
    }, [excalidrawAPI, participants]);
    // looking lock settings
    useEffect(() => {
        if (typeof lockWhiteboard === 'undefined') {
            return;
        }
        if (!(currentUser === null || currentUser === void 0 ? void 0 : currentUser.isRecorder)) {
            setViewModeEnabled(lockWhiteboard);
        }
    }, [lockWhiteboard]);
    // watch presenter changes
    useEffect(() => {
        var _a, _b;
        if (!excalidrawAPI) {
            return;
        }
        if (isPresenter) {
            setViewModeEnabled(false);
            addPreloadedLibraryItems(excalidrawAPI);
        }
        else if (!(currentUser === null || currentUser === void 0 ? void 0 : currentUser.isRecorder)) {
            setViewModeEnabled((_b = (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.metadata) === null || _a === void 0 ? void 0 : _a.lock_settings.lock_whiteboard) !== null && _b !== void 0 ? _b : true);
        }
    }, [excalidrawAPI, isPresenter]);
    // if page change then we'll reset version
    useEffect(() => {
        if (previousPage && whiteboard.currentPage !== previousPage) {
            setLastBroadcastOrReceivedSceneVersion(-1);
        }
        // for recorder & other user we'll clean from here
        if (!isPresenter &&
            previousPage &&
            whiteboard.currentPage !== previousPage) {
            excalidrawAPI === null || excalidrawAPI === void 0 ? void 0 : excalidrawAPI.updateScene({
                elements: [],
            });
            excalidrawAPI === null || excalidrawAPI === void 0 ? void 0 : excalidrawAPI.addFiles([]);
        }
    }, [excalidrawAPI, whiteboard.currentPage, previousPage, isPresenter]);
    const handleRemoteSceneUpdate = (elements, { init = false } = {}) => {
        if (!elements.length) {
            return;
        }
        excalidrawAPI === null || excalidrawAPI === void 0 ? void 0 : excalidrawAPI.updateScene({
            elements,
            commitToHistory: init,
        });
        setLastBroadcastOrReceivedSceneVersion(getSceneVersion(elements));
        // We haven't yet implemented multiplayer undo functionality, so we clear the undo stack
        // when we receive any messages from another peer. This UX can be pretty rough -- if you
        // undo, a user makes a change, and then try to redo, your element(s) will be lost. However,
        // right now we think this is the right tradeoff.
        excalidrawAPI === null || excalidrawAPI === void 0 ? void 0 : excalidrawAPI.history.clear();
    };
    // for handling draw elements
    useEffect(() => {
        // let's wait until fetchedData value change
        // otherwise data won't show correctly.
        if (whiteboard.excalidrawElements && excalidrawAPI && fetchedData) {
            const elements = JSON.parse(whiteboard.excalidrawElements);
            const localElements = excalidrawAPI.getSceneElementsIncludingDeleted();
            const appState = excalidrawAPI.getAppState();
            const reconciledElements = reconcileElements(localElements, elements, appState);
            handleRemoteSceneUpdate(reconciledElements);
        }
    }, [excalidrawAPI, whiteboard.excalidrawElements, fetchedData]);
    // for handling mouse pointer location
    useEffect(() => {
        var _a;
        if (whiteboard.mousePointerLocation) {
            const { pointer, button, name, userId, selectedElementIds } = JSON.parse(whiteboard.mousePointerLocation);
            const tmp = new Map(collaborators);
            const user = (_a = tmp.get(userId)) !== null && _a !== void 0 ? _a : {};
            user.pointer = pointer;
            user.button = button;
            user.selectedElementIds = selectedElementIds;
            user.username = name;
            user.id = userId;
            tmp.set(userId, user);
            excalidrawAPI === null || excalidrawAPI === void 0 ? void 0 : excalidrawAPI.updateScene({ collaborators: tmp });
        }
    }, [whiteboard.mousePointerLocation]);
    // for handling AppState changes
    // websocket will update changes only if current user isn't presenter
    useEffect(() => {
        if (excalidrawAPI && whiteboard.whiteboardAppState) {
            const appState = {
                theme: whiteboard.whiteboardAppState.theme,
                viewBackgroundColor: whiteboard.whiteboardAppState.viewBackgroundColor,
                zenModeEnabled: whiteboard.whiteboardAppState.zenModeEnabled,
                gridSize: whiteboard.whiteboardAppState.gridSize,
            };
            // if width isn't same then we will avoid changes
            // otherwise in small devices it will be problem.
            if (currentWhiteboardWidth >= whiteboard.whiteboardAppState.width) {
                appState.scrollX = whiteboard.whiteboardAppState.scrollX;
                appState.scrollY = whiteboard.whiteboardAppState.scrollY;
                appState.zoom = {
                    value: whiteboard.whiteboardAppState.zoomValue,
                };
            }
            excalidrawAPI.updateScene({
                appState,
            });
        }
    }, [excalidrawAPI, whiteboard.whiteboardAppState]);
    const handleExcalidrawAddFiles = useCallback(async (files) => {
        if (!excalidrawAPI) {
            return;
        }
        const fileReadImages = [];
        const fileReadElms = [];
        for (const file of files) {
            const url = window.PLUG_N_MEET_SERVER_URL +
                '/download/uploadedFile/' +
                file.filePath;
            const canvasFiles = excalidrawAPI.getFiles();
            const elms = excalidrawAPI.getSceneElementsIncludingDeleted();
            let hasFile = false;
            for (const canvasFile in canvasFiles) {
                if (canvasFiles[canvasFile].id === file.id) {
                    // further check if element exist
                    const hasElm = elms.filter((el) => el.id === file.id);
                    if (hasElm.length) {
                        hasFile = true;
                        break;
                    }
                }
            }
            if (!hasFile) {
                const result = await fetchFileWithElm(url, file.id, lastBroadcastOrReceivedSceneVersion, file.isOfficeFile, file.uploaderWhiteboardHeight, file.uploaderWhiteboardWidth);
                if (result && excalidrawAPI) {
                    fileReadImages.push(result.image);
                    fileReadElms.push(result.elm);
                }
            }
        }
        if (!fileReadImages.length) {
            return;
        }
        // need to add all the files at a same time
        // we can't add one by one otherwise file will be missing
        excalidrawAPI.addFiles(fileReadImages);
        fileReadElms.forEach((element) => {
            // it's important to add existing elements too
            // otherwise element will be missing
            const elements = excalidrawAPI
                .getSceneElementsIncludingDeleted()
                .slice(0);
            const hasElm = elements.filter((elm) => elm.id === element.id);
            if (!hasElm.length) {
                // we shouldn't push if element already there.
                // otherwise, it will override if element's position was changed
                elements.push(element);
            }
            excalidrawAPI.updateScene({
                elements,
            });
        });
    }, [excalidrawAPI, lastBroadcastOrReceivedSceneVersion]);
    // for handling files
    useEffect(() => {
        if (whiteboard.whiteboardOfficeFilePagesAndOtherImages && excalidrawAPI) {
            const files = JSON.parse(whiteboard.whiteboardOfficeFilePagesAndOtherImages);
            if (files.length) {
                const currentPageFiles = files.filter((file) => file.currentPage === whiteboard.currentPage);
                handleExcalidrawAddFiles(currentPageFiles);
            }
        }
    }, [
        excalidrawAPI,
        whiteboard.whiteboardOfficeFilePagesAndOtherImages,
        whiteboard.currentPage,
    ]);
    // for refreshing in various reason
    useEffect(() => {
        const doRefresh = throttle(() => {
            if (excalidrawAPI) {
                excalidrawAPI.refresh();
                setCurrentWhiteboardWidth(excalidrawAPI.getAppState().width);
            }
        }, 1000, { trailing: false });
        if (whiteboard.refreshWhiteboard > 0) {
            if (excalidrawAPI) {
                doRefresh();
            }
        }
    }, [whiteboard.refreshWhiteboard]);
    const onChange = (elements, appState) => {
        if (excalidrawAPI && currentUser && elements.length) {
            if (viewModeEnabled) {
                return;
            }
            if (getSceneVersion(elements) > lastBroadcastOrReceivedSceneVersion) {
                setLastBroadcastOrReceivedSceneVersion(getSceneVersion(elements));
                broadcastSceneOnChange(elements, false);
            }
            // broadcast AppState Changes
            if (isPresenter) {
                broadcastAppStateChanges(appState.height, appState.width, appState.scrollX, appState.scrollY, appState.zoom.value, appState.theme, appState.viewBackgroundColor, appState.zenModeEnabled, appState.gridSize);
            }
        }
    };
    const onPointerUpdate = throttle((payload) => {
        if (viewModeEnabled) {
            return;
        }
        if (payload.pointersMap.size < 2 && currentUser) {
            const msg = {
                pointer: payload.pointer,
                button: payload.button || 'up',
                selectedElementIds: excalidrawAPI === null || excalidrawAPI === void 0 ? void 0 : excalidrawAPI.getAppState().selectedElementIds,
                userId: currentUser.userId,
                name: currentUser.name,
            };
            broadcastMousePointerUpdate(msg);
        }
    }, CURSOR_SYNC_TIMEOUT);
    const renderTopRightUI = () => {
        return (_jsx(_Fragment, { children: isPresenter && excalidrawAPI ? (_jsx(ManageFiles, { excalidrawAPI: excalidrawAPI })) : null }));
    };
    const renderFooter = () => {
        return (_jsx(FooterUI, { excalidrawAPI: excalidrawAPI, isPresenter: isPresenter !== null && isPresenter !== void 0 ? isPresenter : false }));
    };
    const handleOnReadyExcalidrawRef = (api) => {
        if (api) {
            excalidrawRefCallback(api);
            onReadyExcalidrawAPI(api);
        }
    };
    return (_jsx(_Fragment, { children: _jsx("div", { className: "excalidraw-wrapper flex-1 w-full max-w-[1200px] m-auto h-[calc(100%-50px)] sm:px-5 mt-9 z-[0]", children: _jsxs(Excalidraw, { excalidrawAPI: (api) => handleOnReadyExcalidrawRef(api), onChange: onChange, onPointerUpdate: onPointerUpdate, viewModeEnabled: viewModeEnabled, isCollaborating: true, theme: theme, name: "plugNmeet whiteboard", UIOptions: {
                    canvasActions: {
                        loadScene: false,
                        export: false,
                        saveAsImage: !(currentUser === null || currentUser === void 0 ? void 0 : currentUser.isRecorder),
                    },
                    tools: {
                        image: false,
                    },
                }, autoFocus: true, detectScroll: true, langCode: i18n.languages[0], renderTopRightUI: renderTopRightUI, 
                //renderFooter={renderFooter}
                libraryReturnUrl: "", children: [_jsxs(MainMenu, { children: [_jsx(MainMenu.DefaultItems.SaveAsImage, {}), _jsx(MainMenu.DefaultItems.Help, {}), screenWidth <= 767 ? renderFooter() : null] }), screenWidth > 767 ? _jsx(Footer, { children: renderFooter() }) : null] }) }) }));
};
_react_refresh_temp_5 = Whiteboard;
$RefreshReg$(_react_refresh_temp_5, "Whiteboard");
_react_refresh_temp_6(Whiteboard, "r/N31fQMk+SGmxctll9KRUchM1E=", false, () => [useTranslation, useCallbackRefState, useAppSelector, useAppSelector, usePreviousFileId, usePreviousPage, useAppSelector, useAppSelector, useAppSelector, useAppSelector]);
export default Whiteboard;
